<template>
  <div class="panels-wrapper">
    <SubscriptionSignup
      :visible="
        route.query.firstLogin === 'true' &&
        !store.getters['user/currentUserSubscriptionActive'] &&
        !isOrgUser &&
        showSubscriptionSignupDialog
      "
      :setVisible="setShowSubscriptionSignupDialog"
    />
    <Onboarding
      v-if="
        route.query.firstLogin === 'true' && !showSubscriptionSignupDialog
      "
      :setJustSubscribed="setJustSubscribed"
    />
    <BaseView>
      <div class="standby-content content-panel">
        <div v-if="!showPrebookedMenu && !showInstantMenu" class="sessions-panel">
          <h1 class="header">Welcome, {{ userFirstName }}</h1>
          
          <section role="group" title="Actions" aria-label="Actions" class="action-button-wrapper">
            <ActionButton 
              type="record"
              :onClick="() => router.push({ name: 'Recorder', params: { recorderType: 'audioRecorder' } })" 
              label="Record Audio"
            />
            <ActionButton 
              type="upload"
              :onClick="() => router.push({ name: 'Upload' })"  
              label="Upload File"
            />
            <ActionButton 
              type="newfolder"
              :onClick="openFolderModal"
              label="Add New Folder"
            />
          </section>

          <section title="Pending Uploads" aria-label="Pending Uploads">
          <div v-if="cachedSessions.length > 0" class="pending-uploads-wrapper">
            <div aria-  class="pending-uploads-warning">
              <img aria-hidden="true" alt="upload icon" :src="UploadWhite" class="pending-uploads-icon" />
              <h5>You have {{ cachedSessions.length }} session{{cachedSessions.length === 1 ? '' : 's'}} to upload</h5>
            </div>
            <div class="pending-uploads">
              <div v-for="item in cachedSessions" :key="item.localSessionId">
                  <div tabIndex="0" class="pending-upload-contents">
                    <span class="upload-tooltip" v-tooltip="'This recording was interrupted before it was saved - we\'ve recovered what we can for you.'" v-if="item.isRecoveredFile">Recovered:&nbsp; {{ item.recordingTitle }}</span>
                    <ProgressBar
                      class="progress-bar"
                      :value="uploadProgress"
                      :showValue="false"
                      v-if="sessionUploadingId === item.localSessionId"
                    />
                    <span class="upload-error" v-if="sessionErrorId == item.localSessionId">Error! {{ uploadErrorMessage }}</span>
                  </div>
                  <div>
                    <RoundedButton
                      class="upload-button"
                      :text="sessionUploadingId === item.localSessionId ? 'Uploading...' : 'Upload'"
                      @Click="resumeSessionUpload(item)"
                      :loading="sessionUploadingId === item.localSessionId"
                      :disabled="sessionUploadingId !== null"
                      :aria-label="`Upload ${item.recordingTitle}`"
                    />
                    <IconButton
                      v-if="sessionUploadingId !== item.localSessionId"
                      :icon="TrashIcon"
                      alt="Remove session"
                      :onclick="() => deleteCachedSession(item)"
                      :enter-press="() => {
                        deleteCachedSession(item)
                      }"
                      dark
                      xsmall
                      ariaLabel="Move to bin"
                    />
                  </div>
                </div>
            </div>
          </div>
          </section>

          <Accordion v-if="isDataLoaded" :value="['0', '1']" multiple>
            <AccordionPanel value="0">
                <AccordionHeader>
                    <span class="accordion-length">({{ allFolders.length }})</span >
                    <span class="accordion-header">My folders </span>
                </AccordionHeader>
                <AccordionContent>
                  <ItemList
                    ref="foldersList"
                    :data="allFolders"
                    :columns="folderColumns"
                    :loader="foldersLoading"
                    :sessionMenuProps="sessionMenuProps"
                    :folderMenuProps="folderMenuProps"
                    :openSession="openSession"
                    :openFolder="goToFolder"
                    :lazyLoading="false"
                    storageKey="folder-table"
                    :scrollHeight="calculatedFolderListHeight"
                  />
                </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="1">
                <AccordionHeader>
                  <span class="accordion-length">({{ allSessions.length || '0' }})</span>
                  <span class="accordion-header">My Sessions </span>
                </AccordionHeader>
                <AccordionContent>
                  <ItemList
                    ref="sessionsList"
                    :data="allSessions"
                    :columns="sessionColumns"
                    :loader="sessionsLoading"
                    :sessionMenuProps="sessionMenuProps"
                    :folderMenuProps="folderMenuProps"
                    :openSession="openSession"
                    :openFolder="goToFolder"
                    :lazyLoading="true"
                    :onLazyLoad="getMoreData"
                    :canLoadMore="canLoadMoreData"
                    storageKey="session-table"
                    :onSortChange="onSortChange"
                    :onSortOrderChange="onSortOrderChange"
                    :scrollHeight="calculatedSessionListHeight"
                  />
                </AccordionContent>
            </AccordionPanel>
          </Accordion>
        </div>
        <div class="standby-card-wrapper" v-else>
          <Card class="join-session-card card">
            <template #content>
              <button
                v-if="!hideBackButton"
                class="back-button"
                @click="
                  () => {
                    showPrebookedMenu = false;
                    showInstantMenu = false;
                  }
                "
                :aria-label="'Back '"
                @keydown="($event) => {
                  if ($event.key === 'Enter') {
                    showPrebookedMenu = false;
                    showInstantMenu = false;
                  }
                }"
              >
                <img :src="ChevronLeftGreyIcon" />
                <span>Back</span>
              </button>
              <div v-if="showPrebookedMenu" class="content">
                <p class="title">
                  Please enter the session ID you wish to access
                </p>
                <div class="input-row">
                  <InputText
                    id="document-name"
                    placeholder="Session ID"
                    type="text"
                    v-model.trim="documentName"
                    v-tooltip.top="'Enter a session ID as found on Beehive'"
                    @keyup.enter="goToSession"
                  />
                  <RoundedButton
                    text="Join"
                    :onClick="goToSession"
                    :enterPress="goToSession"
                    :ariaLabel="`Join session ${documentName}`"
                    accent
                    morePadding
                    lessRound
                    wide
                  />
                </div>
                <div class="error-container">
                  <Transition name="slide-up" mode="out-in">
                    <p v-if="errorMessage" class="input-error">
                      {{ errorMessage }}
                    </p>
                  </Transition>
                </div>
              </div>
              <div v-else class="content">
                <p class="title">Instant Session</p>
                <p class="subtitle">
                  Instantly generate a new Messenger Pigeon session
                </p>
                <RoundedButton
                  text="Start Session"
                  :aria-label="'Start session'"
                  :enterPress="() => goToSessionSetup"
                  :onClick="goToSessionSetup"
                  slim
                  fitWidth
                  lessRound
                  accent
                />
              </div>
            </template>
          </Card>
        </div>
      </div>
    </BaseView>
    <ConfirmationModal
        class="confirmation-modal"
        modal
        header="Delete recording?"
        buttonTextConfirm="Delete"
        buttonTextCancel="Cancel"
        smallerTitle
        ref="confirmationModalRef"
      >
        <p>
          This session hasn't finished uploading.
        </p>
        <p>
          If you delete it, we won't be able to recover it.
        </p>
      </ConfirmationModal>
      
  </div>
  <TitleChangeModal
    :session-title="selectedTitle"
    :session-id="selectedId"
    :itemId="selectedId"
    :itemTitle="selectedTitle"
    :itemType="selectedType"
    :visible="titleChangeModalVisible"
    :setVisible="setTitleChangeModal"
  />
  <RequestNotesOrderForm
    :visible="orderFormVisible"
    :recordingTitle="sessionTitle"
    :durationHMS="durationHMS"
    :durationMilliseconds="durationMilliseconds"
    :recordingUploadId="recordingUploadId"
    :setOrderFormVisible="setOrderFormVisible"
    @hide="clearOrderFormValues"
    @updateStandbySessionsMenuItem="getUserSessionInfo"
    ref="orderForm"
  ></RequestNotesOrderForm>
  <RowActionMenubar
    v-if="sessionsListComponent"
    :toggleSessionMenu="sessionsListComponent?.toggleSessionMenu"
    :visible="rowActionVisible"
    @rename-item="openTitleChangeModal"
    @move-item="openMoveSessionModal"
  />
  <MoveSessionModal
    ref="moveSessionModal"
    @reload-page="initPageData"
  />
  <FolderModal
    :visible="folderModalVisible"
    :setVisible="setFolderModal"
  />
    <Toast
    position="top-center"
    group="notification"
    class="itemlist-notification-toast-wrapper"
  ></Toast>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeMount, onBeforeUnmount, watch, useTemplateRef } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useDebounceFn } from '@vueuse/core';
import BaseView from "../views/BaseView.vue";
import RoundedButton from "../components/RoundedButton.vue";
import ChevronLeftGreyIcon from "../assets/images/Chevron-left-grey.svg";
import Onboarding from "../components/Onboarding/Onboarding.vue";
import SubscriptionSignup from "../components/Onboarding/SubscriptionSignup.vue";
import { uploadMedia, recoverRecordings, tidyUploadCache, eventEmitter } from "../assets/uploadFile.js";
import UploadWhite from "../assets/images/Upload-white.svg";
import TrashIcon from "../assets/images/Trash-white.svg";
import ConfirmationModal from "../components/ConfirmationModal.vue";
import ItemList from "../components/Home/ItemList.vue";
import RowActionMenubar from "../components/Home/RowActionMenubar.vue";
import RequestNotesOrderForm from "../components/Orders/RequestNotesOrderForm.vue";
import TitleChangeModal from "../components/TitleChangeModal.vue";
import ActionButton from '../components/Home/ActionButton.vue';
import FolderModal from "../components/FolderModal.vue";
import MoveSessionModal from "../components/MoveSessionModal.vue";
import { useOrderForm } from "@/composables/useOrderForm";
import { useTitleChangeModal } from '@/composables/useTitleChangeModal';
import { useSupplementaryUpload } from '@/composables/useSupplementaryUpload';
import { SessionWithOrdersAndType, CachedLocalSession } from '@/types/session-interfaces';
import { Folder } from '@/types/folder-interfaces';
import { Order, OrderStatus } from '@/types/order-interfaces';
import { ItemListColumn } from '@/types/itemlist-interfaces';
import IconButton from '@/components/IconButton.vue';

// UUID validation regex
const uuidV4Regex = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i;

// Setup router and store
const router = useRouter(),
      route = useRoute(),
      store = useStore();

// Import composables
const {
  orderFormVisible,
  sessionTitle,
  requestNotes,
  durationHMS,
  durationMilliseconds,
  recordingUploadId,
  setOrderFormVisible,
} = useOrderForm();

const { triggerFileUpload } = useSupplementaryUpload();

const {
  titleChangeModalVisible,
  selectedType,
  selectedTitle,
  selectedId,
  openTitleChangeModal,
  setTitleChangeModal,
} = useTitleChangeModal();

// Refs for template elements
const confirmationModal = ref(),
      foldersListComponent = ref();

// This needs to be a computed property to ensure it updates AFTER the ItemList renders
// as the RowActionMenubar is only mounted after the ItemList is rendered
const sessionsListComponent:any = computed(() => sessionsList.value);

// State variables
const documentName = ref<string>(""),
      notetakerId = ref<string>(""),
      errorMessage = ref<string>(""),
      showPrebookedMenu = ref<boolean>(false),
      showInstantMenu = ref<boolean>(false),
      hideBackButton = ref<boolean>(false),
      justSubscribed = ref<boolean>(false),
      showSubscriptionSignupDialog = ref<boolean>(true),
      sessionUploadingId = ref<string | null>(null),
      sessionErrorId = ref<string>(""),
      uploadErrorMessage = ref<string>(""),
      uploadProgress = ref<number>(0),
      uploadButtonText = ref<string>("Upload"),
      sessionsLoading = ref<boolean>(true),
      foldersLoading = ref<boolean>(true),
      folderModalVisible = ref<boolean>(false),
      moveSessionModal = ref(),
      sessionSortField = ref<string>(""),
      sessionSortOrder = ref<string>("");

// template refs for components
const sessionsList = useTemplateRef("sessionsList"),
      foldersList = useTemplateRef("foldersList"),
      orderFormRef = useTemplateRef("orderForm"),
      confirmationModalRef = useTemplateRef("confirmationModalRef"),
      moveSessionModalRef = useTemplateRef("moveSessionModal");

// Table related state
const folderColumns = ref<ItemListColumn[]>([]);
const sessionColumns = ref<ItemListColumn[]>([]);

const sessionMenuProps = ref<Array<{
  label?: string;
  icon?: string;
  // eslint-disable-next-line no-unused-vars
  command?: (rowData: any) => void;
  // eslint-disable-next-line no-unused-vars
  checkLabel?: (rowData: any) => string;
  // eslint-disable-next-line no-unused-vars
  checkVisible?: (rowData: any) => boolean;
  // eslint-disable-next-line no-unused-vars
  checkDisable?: (rowData: any) => boolean;
}>>([]);
// eslint-disable-next-line no-unused-vars
const folderMenuProps = ref<{ label: string; icon: string; command: (rowData: any) => void; }[]>([]);

// User info
const userFirstName = computed(() => store.state.user.currentUser.self.name.firstName || '');

// Watchers for error message clearing
watch(documentName, (newName) => {
  if (newName !== "") {
    errorMessage.value = '';
  }
});

watch(notetakerId, (newName) => {
  if (newName !== "") {
    errorMessage.value = '';
  }
});

// We want our lists to flex below a certain number of items, so we calculate the height based on
// the number of items
//
// Once the number of items exceeds a certain threshold, we set a fixed height and let the list's
// internal scrolling handle the overflow
//
// We can't just use `flex` because virtual scrolling doesn't work well with flexbox in PrimeVue
//
// `idealRowHeight` is guesstimated based on the row element height, gap, padding, etc.
// We then add a bit more at the end to account for headers, padding on the table, etc.
const idealRowHeight = 10.2

const calculatedFolderListHeight = computed(() => {
  if (!allFolders.value.length) {
    return `${idealRowHeight + 12}rem`;
  }
  return `${((allFolders.value.length >= 4) ? (idealRowHeight * 4) : (idealRowHeight * allFolders.value.length)) + 12}rem`;
});

const calculatedSessionListHeight = computed(() => {
  if (!allSessions.value.length) {
    return `${idealRowHeight + 20}rem`;
  }
  return `${((allSessions.value.length >= 10) ? (idealRowHeight * 10) : (idealRowHeight * allSessions.value.length)) + 20}rem`;
});

// Methods

/**
 * When the session sort field or order changes, this function will be called to reload the sessions.
 * We debounce it because both the sort field and order can change at the same time, so we don't want
 * to reload the sessions multiple times in quick succession.
 */
const debouncedReloadSessions = useDebounceFn(() => {
  store.dispatch('itemList/reloadSessions', {sortField: sessionSortField.value, sortOrder: sessionSortOrder.value}).then(() => {
    sessionsLoading.value = false;
    foldersLoading.value = false;
  }).catch((error) => {
    console.error('Error reloading sessions:', error);
    sessionsLoading.value = false;
    foldersLoading.value = false;
  });
}, 1000, { maxWait: 3000 })

const onSortChange = async (sortField: string) => {
  if (sessionSortField.value === sortField) return;
  sessionSortField.value = sortField
  updateDataTableState(sortField, undefined);
  sessionsLoading.value = true
  debouncedReloadSessions()
}

const onSortOrderChange = async (sortOrder: number|undefined) => {
  const sortOrderValue = sortOrder && sortOrder > 0 ? 'asc' : 'desc';
  if (sessionSortOrder.value === sortOrderValue) return;
  sessionSortOrder.value = sortOrderValue;
  updateDataTableState(undefined, sortOrder);
  sessionsLoading.value = true
  debouncedReloadSessions()
}

const updateDataTableState = (sortField?: string, sortOrder?: number) => {
  const currentState = JSON.parse(localStorage.getItem("session-table") || "{}");
  if (sortField !== undefined) currentState.sortField = sortField;
  if (sortOrder !== undefined) currentState.sortOrder = sortOrder;
  localStorage.setItem("session-table", JSON.stringify(currentState));
}

const setShowSubscriptionSignupDialog = (isVisible: boolean) => {
  showSubscriptionSignupDialog.value = isVisible;
};

const openFolderModal = () => {
  folderModalVisible.value = true;
};

const setFolderModal = (isVisible: boolean) => {
  folderModalVisible.value = isVisible;
};

const goToSession = () => {
  if (documentName.value === "") {
    errorMessage.value = "Blank session id! Please enter a valid session ID.";
  } else {
    const isValidUUID = uuidV4Regex.test(documentName.value);
    if (isValidUUID) {
      store.commit("user/setSessionDetails", {
        id: documentName.value,
      });
      router.push({
        name: "SessionInitializer",
      });
    } else {
      errorMessage.value = "The session id format is invalid.";
    }
  }
};

const goToSessionSetup = () => {
  router.push({
    name: "SessionSetup",
    query: { hideBackButton: String(hideBackButton.value)},
  });
};

const setJustSubscribed = (subscribed: boolean) => {
  justSubscribed.value = subscribed;
};

const resumeSessionUpload = (cachedSession: CachedLocalSession) => {
  sessionErrorId.value = '';
  uploadErrorMessage.value = '';
  sessionUploadingId.value = cachedSession.localSessionId;
  uploadButtonText.value = "Uploading...";
  
  uploadMedia(
    cachedSession.recordingTitle,
    cachedSession.recordingDuration,
    cachedSession.recordingDurationHMS,
    cachedSession.recordingCachePath,
    cachedSession.uploadType,
    cachedSession.uploadType == "mp-video",
    cachedSession.localSessionId,
    cachedSession,
    store
  ).then((uploadDataObject) => {
    if(uploadDataObject.errorMessage) {
      sessionErrorId.value = uploadDataObject.localSessionId;
      uploadErrorMessage.value = uploadDataObject.errorMessage;
    }
    
    sessionUploadingId.value = null;
  });
};

const deleteCachedSession = async (cachedSession: CachedLocalSession) => {
  const confirmed = await confirmationModal.value.show();

  if(confirmed) {
    tidyUploadCache(cachedSession, store);
  }
};

const open = (data: SessionWithOrdersAndType, status: string, statusDetail: OrderStatus, order: Order) => {
  store.commit("user/setSessionDetails", {
    status: status,
    statusDetail: statusDetail,
    order: order,
    ...data,
  });
  router.push({
    name: "SessionInitializer",
  });
};

const getUserSessionInfo = async () => {
  sessionsLoading.value = true;
  foldersLoading.value = true;

  folderColumns.value = [
    { 
      field: "title", 
      header: "TITLE", 
      sortable: true, 
      width: "79.77%",
    },
    {
      field: "createdAt",
      header: "CREATED",
      sortable: true,
      width: "15.36%",
    },
    { 
      field: "num", 
      header: "", 
      icon: true, 
      class: "small",
      width: "4.86%",
    },
  ];

  sessionColumns.value = [
    { 
      field: "title", 
      header: "TITLE", 
      sortable: true, 
      width: "44.44%",
    },
    {
      field: "noteStatus",
      header: "NOTES STATUS",
      sortable: false,
      width: "14.58%",
    },
    {
      field: "uploads",
      sortable: false,
      sortFunction: (session1: SessionWithOrdersAndType, session2: SessionWithOrdersAndType) => {
        return (session1.uploads?.length ?? 0) - (session2.uploads?.length ?? 0);
      },
      header: "SUPPLEMENTARY FILES",
      width: "20.75%",
    },
    {
      field: "createdAt",
      header: "CREATED",
      sortable: true,
      width: "15.36%",
    },
    { 
      field: "num", 
      header: "", 
      icon: true, 
      class: "small",
      width: "4.86%",
    },
  ]

  sessionMenuProps.value = [
    {
      label: "Open",
      icon: "itemlist-open-svg-icon",
      command: (rowData) => {
        // TODO: fix this handling when Anthill status is fixed
        if (
          rowData.type === "session" &&
          rowData.uploads.anthillProjectStatus &&
          rowData.uploads.anthillProjectStatus === "Done"
        ) {
          open(rowData, rowData.noteStatus, rowData.orderStatus, rowData.orders);
        } else {
          open(rowData, rowData.noteStatus, rowData.orderStatus, rowData.orders);
        }
      },
      // checkDisabled: (rowData) => !rowData.uploads.anthillProjectStatus,
    },
    {
      label: "Request Professional Note",
      command: (rowData) => {
        requestNotes(rowData);
      },
      icon: "itemlist-requestnotes-svg-icon",
      checkLabel: (rowData) => {
        return rowData.noteStatus !== "no-value" ? "Notes Requested" : "Request Professional Notes";
      },
      checkVisible: (rowData) => {
        return !(rowData.uploads && rowData.uploads[0] && rowData.uploads[0].type === "mp-document") && 
          !(rowData.uploads && rowData.uploads[0] && rowData.uploads[0].type === "mp-instant-session") &&
          hasProNoteAccess.value;
      },
      checkDisable: (rowData) => { return rowData.noteStatus !== "no-value"; }
    },
    {
      label: "Add Supplementary Material",
      command: (rowData) => {
        triggerFileUpload(rowData.id);
      },
      icon: "itemlist-upload-white-svg-icon",
      checkDisable: (rowData) => { return rowData.uploads.filter((upload: any) => upload.type === "mp-supplementary").length > 4; }
    },
    {
      label: "Copy Session ID",
      command: (rowData) => {
        copySessionId(rowData.id);
      },
      icon: "itemlist-copy-svg-icon",
    },
    {
      label: "Rename",
      icon: "itemlist-rename-svg-icon",
      command: (rowData) => {
        openTitleChangeModal(rowData);
      },
    },
  ];

  folderMenuProps.value = [
    {
      label: "Open",
      icon: "itemlist-open-svg-icon",
      command: (rowData) => {
        goToFolder(rowData);
      },
    },
    {
      label: "Rename",
      icon: "itemlist-rename-svg-icon",
      command: (rowData) => {
        openTitleChangeModal(rowData);
      },
    },
  ];

  sessionsLoading.value = false;
  foldersLoading.value = false;
};

const getMoreData = async () => {
  await store.dispatch('itemList/fetchAllDataMore', {sortField: sessionSortField.value, sortOrder: sessionSortOrder.value});
}

const canLoadMoreData = computed(() => {
  if (!store.state.itemList.sessionPagination || !store.state.itemList.folderPagination) {
    return true;
  }

  const canLoadMoreSessions = store.state.itemList.sessionPagination.nextPage !== null;
  const canLoadMoreFolders = store.state.itemList.folderPagination.nextPage !== null;

  return canLoadMoreFolders || canLoadMoreSessions;
});

const openSession = (session: SessionWithOrdersAndType) => {
  store.commit("user/setSessionDetails", {
    status: session.noteStatus,
    statusDetail: session.orderStatus,
    order: session.orders,
    ...session,
  });
  router.push({
    name: "SessionInitializer",
  });
};

const goToFolder = (folder: Folder) => {
  router.push(`/folder/${folder.id}?title=${folder.title}`);
};

const clearOrderFormValues = () => {
  if (orderFormRef.value) {
    orderFormRef.value.orderSubmitted = false;
    orderFormRef.value.step = 0;
    orderFormRef.value.orderSubmissionErrorMessage = "";
  }
};

const copySessionId = (sessionId: string): void => {
  console.debug("RowData ", sessionId);
  try {
    navigator.clipboard.writeText(sessionId);
  } catch (error: unknown) {
    console.log(error);
  }
};

const setupTableInfo = async () => {

      await getUserSessionInfo();

      sessionsLoading.value = false;
      foldersLoading.value = false;
      
};

const openMoveSessionModal = async () => {
  await moveSessionModal.value?.show();
};

const isOrgUser = computed(() => {
  return store.state.user.currentUser.type.toLowerCase().trim() === "org user";
});

const hasProNoteAccess = computed(() => {
  return store.getters['user/hasProNotesEnabled'];
});

const cachedSessions = computed(() => {
  return store.getters['user/cachedSessions'];
});

const allItems = computed({
  get: () => store.getters['itemList/getAllItems'],
  set: (val) => store.dispatch('itemList/updateAllItems', val)
});

const allFolders = computed({
  get: () => store.getters['itemList/getAllFolders'],
  set: (val) => store.dispatch('itemList/updateAllFolders', val)
});

const allSessions = computed({
  get: () => store.getters['itemList/getAllSessions'],
  set: (val) => store.dispatch('itemList/updateAllSessions', val)
});

const isDataLoaded = computed(() => {
  return allItems.value.length > 0;
});

const rowActionVisible = computed(() => store.state.itemList.selectedItems.length > 0);

const initPageData = async () => {
  const sortOptions = localStorage.getItem("session-table")
  let sortField = "createdAt"
  let sortOrder = "desc"
  if(sortOptions) {
    const parsedOptions = JSON.parse(sortOptions)
    sortField = parsedOptions.sortField
    sortOrder = parseInt(parsedOptions.sortOrder) > 0 ? 'asc' : 'desc'
  }
  await store.dispatch('itemList/fetchAllData', {sortField, sortOrder});
};

// Lifecycle hooks
onBeforeMount(async () => {
  if (!isDataLoaded.value) {
    // Read in any persisted default sort options from localStorage
    // and use them to fetch the initial data for the sessions list.
    const sortOptions = localStorage.getItem("session-table")
    let sortField = "createdAt"
    let sortOrder = "desc"
    if(sortOptions) {
      const parsedOptions = JSON.parse(sortOptions)
      sortField = parsedOptions.sortField
      sortOrder = parseInt(parsedOptions.sortOrder) > 0 ? 'asc' : 'desc'
    }
    await store.dispatch('itemList/fetchAllData', {sortField, sortOrder});
  }
});

onMounted(async () => {
  //Clear any corrupted sessions
  store.commit('user/removeCorruptedCachedSessions');

  //Attempt to recover any lost recordings
  recoverRecordings(store);

  eventEmitter.on("uploadProgress", ({localSessionId, progress}) => {
    if(localSessionId == sessionUploadingId.value) {
      uploadProgress.value = progress * 100.0;
    }
  });

  await setupTableInfo();
  foldersListComponent.value = foldersList.value;
  confirmationModal.value = confirmationModalRef.value;
  moveSessionModal.value = moveSessionModalRef.value;
});

onBeforeUnmount(() => {
  store.commit("itemList/clearSelection");
  eventEmitter.removeAllListeners("uploadProgress");
});
</script>

<style lang="scss">
.left-wrapper {
  --p-transition-duration: 0.1s;
  position: relative;

  .side-panel {
    background-color: map-get($component-colours, chat-background);
    color: map-get($component-colours, chat-font-primary);
    min-width: $side-panel-width;
    position: absolute;
    top: 0;
    left: 0;
    width: $side-panel-width;
    height: 100%;
    z-index: 2;

    @media only screen and (max-width: 1000px) {
      min-width: $side-panel-width-mobile;
      width: $side-panel-width-mobile;
    }

    ::selection {
      background-color: map-get(
        $component-colours,
        font-background-colour-highlighted
      );
    }
    ::-moz-selection {
      background-color: map-get(
        $component-colours,
        font-background-colour-highlighted
      );
    }
  }
}
.standby-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 4rem 5.6rem;
  overflow: auto;

  .pending-uploads-wrapper {

    font-size: 1.3rem;

    .pending-uploads-warning {
      font-size: 1.3rem;
      padding-left: 8px;
      display: flex;
      justify-content: flex-start;
      gap: 6px;
      align-items: center;
      margin-bottom: 12px;

      .pending-uploads-icon {
        height: 24px;
        width: 24px;
      }
    }

    .pending-uploads {

        border-radius: 12px;
        background-color: map-get($colours, accent-purple-4);
        border: 1px solid map-get($colours, accent-purple-3);

        &:focus-visible {
          box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
          outline: solid 0.2rem rgba(255, 233, 254, 1);
          border: solid 0.1rem map-get($colours, accent-purple3);
        }

      div {
        display: flex;
        padding: 6px 6px 6px 12px;
        justify-content: space-between;
        align-items: center;

        .pending-upload-contents {
          flex: 1;
          justify-content: flex-start;
        }

        .progress-bar {
          width: 50%;
          height: 0.6rem;
          margin: 0 auto;
          padding: 0;
        }
        .p-progressbar {
          background-color: map-get($colours, accent-purple-2);
          border-radius: 0.6rem;
        }

        .p-progressbar .p-progressbar-value {
          padding: 0;
          background-color: map-get($colours, green);
        }

        .upload-button {
          color: white;
          border-radius: 8px;
          padding: 6px 12px;
          background-color: map-get($colours, accent-purple-2);
          cursor: pointer;
          display: flex;
          margin-right: 12px;

          &:disabled {
            opacity: 0.5;
            pointer-events: none;
            cursor: auto;
          }
        }

        .upload-error {
          text-align: center;
          color: map-get($colours, red);
        }

        img {
          cursor: pointer;

          &.disabled {
            opacity: 0.2;
            pointer-events: none;
            cursor: auto;
          }
        }

        .upload-tooltip {
          font-weight: bold;
          cursor: help;
        }
      }

    }
  }
  .p-accordionheader {   
    flex-direction: row-reverse;
    justify-content: flex-end;
    gap: 0.6rem;

    &:focus {
      box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
      outline: solid 0.2rem rgba(255, 233, 254, 1);
      border: solid 0.1rem map-get($colours, accent-purple3);
    }

    &-toggle-icon {
      width: 1.6rem;
    }

    .accordion-header-wrapper {
      display: flex;
      align-items: center;
    }

    .accordion-header {
      @include h3;
    }
    .accordion-length {
      padding-bottom: 0.2rem;
      @include callout;
      color: map-get($colours, grey-2);
      line-height: 2.4rem;
    }
  }

  .p-accordionpanel {
    border: none;
  }

  .sessions-panel {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;  
    .header {
      @include h2-accent;
    }

    .subheader {
      @include section-sub-header;
    }

    .action-button-wrapper{
      width: 44.6rem;
      height: 8rem;
      flex-grow: 0;
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
      gap: 1.6rem;
      padding: 0;
    }

    .button-wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      overflow: auto;
      gap: 1rem 1.5rem;

      @media screen and (max-width: 600px) {
        justify-content: flex-start;
        * {
          flex: 1 0 100%;
        }
      }
    }
  }

  .menu-item-focus {
    &:focus-within {
      border: solid 1px rgba(255, 233, 254, 1);
      outline: solid 1px rgba(255, 233, 254, 1);
      box-shadow: 0 0 0 2.5px rgba(255, 233, 254, 0.4);
    }
  }

  .standby-card-wrapper {
    padding: 3.2rem;
    margin: auto;

    .card {
      width: 34rem;
      height: 50rem;
      padding: 3.2rem 2.4rem;
    }

    .standby-card {
      font-size: 1.4rem;
      font-family: $font-secondary;
      display: flex;
      flex-direction: column;
      align-items: center;

      .p-card-body {
        height: 100%;
        display: flex;
        flex-direction: column;

        .p-card-content {
          flex: 1;
          padding: 0;

          .content {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
        }
      }

      .title {
        text-align: center;
        margin: 0;
      }

      .pigeon {
        font-size: 2rem;
      }

      .placeholder {
        font-size: 1rem;
        visibility: hidden;
      }

      .buttons {
        > * {
          width: 33%;
          // margin-left: 1rem;
        }
      }

      .subtitle {
        @include section-header-3;
        margin-top: 3.2rem;
        margin-bottom: 0.8rem;
      }

      .session-type-button {
        text-align: left;
        border-bottom: 0.1rem solid grey;
        padding: 1.6rem 0;
        display: flex;
        flex-direction: row;
        align-items: center;

        img {
          transition: 0.4s;
        }

        &:hover,
        &:active {
          &:not([disabled]) {
            filter: none;
          }

          img {
            transform: scale(1.5) translateX(0.4rem);
          }
        }

        .text-content {
          header {
            @include h3;
          }

          main {
            @include callout;
            color: map-get($colours, grey-5);
            margin-top: 0.8rem;
          }
        }
      }
    }

    .join-session-card {
      padding: 0 2rem;

      .back-button {
        display: flex;
        align-items: center;
        color: map-get($colours, grey-3);
        padding: 0;
        margin-left: -0.8rem;
        @include section-header-3;
        margin-top: 0.4rem;

        &:hover,
        &:active {
          &:not([disabled]) {
            filter: none;
            text-decoration: underline;
          }
        }

        span {
          margin-top: 0.2rem;
        }
      }

      .content {
        margin-top: 4.8rem;

        .title {
          @include section-header-3;
          margin-bottom: 1.6rem;
        }

        .subtitle {
          @include callout;
          color: map-get($colours, grey-5);
          margin: 1.6rem 0;
        }

        .input-row {
          display: flex;

          input {
            flex: 1;
            height: 3.6rem;
            font-size: 1.4rem;
            margin-right: 0.8rem;
          }
        }
      }
    }
  }

  .session-id-input {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    
    .session-id-input-title {
      font-size: 2rem;
    }

    .session-id-input-input {
      width: 38.8rem;
      padding: 1rem 3.2rem 1rem 1.2rem;
      color: white;
      background-color: map-get($colours, accent-purple-2);
      &:focus {
        background-color: map-get($colours, accent-purple-2);
        border: solid 0.1rem map-get($colours, grey-2);
      }

      &:hover {
        border: solid 0.1rem map-get($colours, grey-2);
      }
    }

    .session-id-input-error-space {
      margin-bottom: 4.6rem;
    }
  }
}
.document-wrapper {
  display: flex;
  margin-bottom: 0.6rem;

  .p-float-label {
    flex: 1;
    margin-right: 0.6rem;

    input {
      width: 100%;
    }
  }

  button {
    font-size: 1.4rem;
  }
}

.input-error {
  color: map-get($component-colours, font-colour-error);
  font-size: 1.4rem;
}

.session-id-dialog {
  border-radius: 6px;
  position: relative;

  .p-dialog-header {
    padding: 4.5rem 4rem 1.6rem 4rem;
    border-radius: 6px 6px 0 0;

    .p-dialog-title {
      flex-grow: 1;
      text-align: center;
      @include h1;
      font-family: $font-accent;
    }

    .p-dialog-header-icons {
      position: absolute;
      right: 1.25rem;
      top: 1.25rem;
    }
  }

  .p-dialog-content {
    padding: 0 4rem 0 4rem;

    .p-inputtext {
      width: 100%;
    }
  }
  .p-dialog-footer {
    padding: 4rem 4rem 4rem 4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;
  }
}

.v-popper__popper .v-popper__inner {
  font-size: 1.2rem;
  padding: 12px;
}

.itemlist-upload-white-svg-icon {
  background: url("../assets/images/UploadSupplementaryMaterialsIcon.svg");
}

.itemlist-requestnotes-svg-icon {
  background: url("../assets/images/Notes-white-requested.svg");
}

.itemlist-open-svg-icon {
  background: url("../assets/images/OpenIcon.svg");
}

.itemlist-rename-svg-icon {
  background: url("../assets/images/Pencil-grey.svg");
}

.itemlist-copy-svg-icon {
  background: url("../assets/images/Copy-white-small.svg");
}
</style>